/**
 * A sales demo's tutorial (SDK 1.12, the extension's `tutorial` steps): it starts by itself once
 * per sign-in (model.ts), when the page is one of the menu's views, so a sign-in that lands on the
 * product tour or Users & roles waits until the reader opens one; and again from the user menu
 * (`start`). Skip ends it for this sign-in; the next sign-in starts it again.
 * driver.js and the tutorial's styles load only when it starts (runner.ts).
 */
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { APP_BASE } from '../base'
import { markSeen, seenThisSignIn } from './model'
import type { ResolvedStep } from './model'
import type { TutorialRun } from './runner'
import './i18n'

/** The app's current address (as the router sees it), read at once rather than after a render. */
function currentAddress(): string {
    return `${window.location.pathname.slice(APP_BASE.length - 1)}${window.location.search}`
}

export function useTutorial({ slug, signIn, steps, autoStart }: {
    slug: string
    /** The sign-in it belongs to (model.ts signInKey). */
    signIn: string
    /** This tenant's steps in the reader's language; none: no tutorial. */
    steps: ResolvedStep[]
    /** The page may start it by itself (a view of the menu is open). */
    autoStart: boolean
}): { available: boolean; start: () => void } {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const run = useRef<TutorialRun | null>(null)
    const starting = useRef(false)
    const mounted = useRef(true)
    // The steps as of the last render (a new array each render), for `start` to read.
    const latest = useRef(steps)
    useEffect(() => {
        latest.current = steps
    })

    const start = useCallback(() => {
        const steps = latest.current
        if (run.current || starting.current || steps.length === 0) return
        starting.current = true
        void import('./runner')
            .then(({ runTutorial }) => {
                if (!mounted.current || run.current) return
                run.current = runTutorial({
                    steps,
                    labels: { next: t('tutorial.next'), back: t('tutorial.back'), done: t('tutorial.done'), skip: t('tutorial.skip') },
                    navigate: (to) => navigate(to),
                    here: currentAddress,
                    onEnd: () => {
                        run.current = null
                    },
                })
            })
            .catch(() => {
                // Its code didn't load (a deploy replaced the build): the menu can start it after a reload.
            })
            .finally(() => {
                starting.current = false
            })
    }, [t, navigate])

    // Once per sign-in, marked when it starts: a reload mid-way doesn't start it over. Never by
    // itself in a browser a script drives (navigator.webdriver): the "How it works" film's
    // recorder (scripts/tour-video.mjs) signs in to a demo, and so do the browser tests; the
    // user menu still starts it there.
    useEffect(() => {
        if (!autoStart || steps.length === 0 || navigator.webdriver || seenThisSignIn(slug, signIn)) return
        markSeen(slug, signIn)
        start()
    }, [autoStart, steps.length, slug, signIn, start])

    // The reader moved (Back, the corner button on the last step): the tutorial ends.
    useEffect(() => {
        run.current?.moved()
    }, [location.pathname, location.search])

    // Signed out (the layout goes): so does the tutorial.
    useEffect(() => {
        mounted.current = true
        return () => {
            mounted.current = false
            run.current?.stop()
            run.current = null
        }
    }, [])

    return { available: steps.length > 0, start }
}
